import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  Paper,
  useTheme,
  CircularProgress
} from '@mui/material';
import { Download, Close } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BusTicketInvoice = ({ data, onClose }) => {
  const theme = useTheme();
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);

  const handleDownload = async () => {
    setIsGeneratingPDF(true);
    const input = document.getElementById('bus-ticket-invoice');
    const buttons = document.getElementById('pdf-buttons-container');
    
    // Store original styles
    const originalWidth = input.style.width;
    const originalOverflow = input.style.overflow;
    const originalBackground = input.style.background;
    const originalButtonsDisplay = buttons.style.display;
    
    try {
      // Apply PDF generation styles
      input.style.width = '800px';
      input.style.overflow = 'visible';
      input.style.background = '#ffffff';
      buttons.style.display = 'none'; // Hide buttons for PDF

      const options = {
        scale: 3,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: document.documentElement.scrollWidth,
        width: 800,
        height: input.scrollHeight,
        logging: false,
        letterRendering: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      };

      const canvas = await html2canvas(input, options);
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Calculate PDF dimensions
      const pdfWidth = 210; // A4 width in mm (portrait)
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      // Create PDF with multi-page support
      const pdf = new jsPDF({
        orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      // Check if content exceeds single page
      const pageHeight = pdf.internal.pageSize.getHeight();
      if (pdfHeight > pageHeight) {
        const remainingHeight = pdfHeight - pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -pageHeight, pdfWidth, pdfHeight);
      }
      
      pdf.save(`bus-ticket-${data.ticketNo || Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // Restore original styles
      input.style.width = originalWidth;
      input.style.overflow = originalOverflow;
      input.style.background = originalBackground;
      buttons.style.display = originalButtonsDisplay;
      setIsGeneratingPDF(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      weekday: 'short'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Paper 
        id="bus-ticket-invoice"
        elevation={3}
        sx={{ 
          p: 3,
          width: '800px',
          fontFamily: 'Roboto, Arial, sans-serif',
          background: 'linear-gradient(to bottom, #f9f9f9, #ffffff)',
          boxSizing: 'border-box',
          margin: '0 auto'
        }}
      >
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${theme.palette.primary.main}`
        }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold', 
              color: theme.palette.primary.dark,
              lineHeight: 1.2
            }}>
              E-Ticket
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Booking ID: {data.ticketNo || 'N/A'}
            </Typography>
          </Box>
          
          <Box sx={{ 
            backgroundColor: theme.palette.success.light,
            p: 1,
            borderRadius: '4px',
            textAlign: 'center',
            minWidth: '120px'
          }}>
            <Typography variant="body2" sx={{ 
              fontWeight: 'bold',
              color: theme.palette.success.dark
            }}>
              CONFIRMED
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatDate(data.createdDate)}
            </Typography>
          </Box>
        </Box>

        {/* Important Notice */}
        <Box sx={{ 
          backgroundColor: '#fff8e1', 
          p: 2, 
          mb: 4, 
          borderRadius: '8px',
          borderLeft: `4px solid ${theme.palette.warning.main}`
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            <span style={{ color: theme.palette.warning.dark }}>⚠️ TRAVEL REMINDER:</span>
          </Typography>
          <Box component="ul" sx={{ 
            pl: 2, 
            mb: 0,
            '& li': { 
              mb: 1,
              fontSize: '0.9rem'
            }
          }}>
            <li>Carry this e-ticket + original ID proof</li>
            <li>Arrive <strong>30 minutes before</strong> departure</li>
            <li>Check terminal/boarding point details</li>
          </Box>
        </Box>

        {/* Passenger & Journey Details */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ 
              p: 2, 
              height: '100%', 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px'
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                color: theme.palette.primary.main
              }}>
                Passenger Information
              </Typography>
              
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <DetailItem label="Name" value={data.buspassengerfname} important />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Contact" value={data.contactNo} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Seat No." value={data.seatName} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Seat Type" value={data.seatType} />
                </Grid>
                <Grid item xs={12}>
                  <DetailItem label="PNR Number" value={data.travelOperatorPNR} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ 
              p: 2, 
              height: '100%', 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px'
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                color: theme.palette.primary.main
              }}>
                Journey Details
              </Typography>
              
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <DetailItem 
                    label="Route" 
                    value={`${data.origin || 'N/A'} to ${data.destination || 'N/A'}`} 
                    important
                  />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Travels" value={data.travelName} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Bus Type" value={data.busType} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Date" value={formatDate(data.dateOfJourney)} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Departure" value={formatTime(data.departureTime)} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Boarding Point */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 4,
          mt: 2, 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px'
        }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 'bold', 
            mb: 2,
            color: theme.palette.primary.main
          }}>
            Boarding Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Boarding Point
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Dange Chowk
              </Typography>
              <Typography variant="body2">
                Pune - Mumbai Highway<br />
                Near City Mall
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DetailItem label="Boarding Time" value={formatTime(data.departureTime)} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem label="Report By" value={
                    formatTime(new Date(data.departureTime).setMinutes(
                      new Date(data.departureTime).getMinutes() - 30
                    ))
                  } />
                </Grid>
                <Grid item xs={12}>
                  <DetailItem label="Contact" value={data.contactNo || '9021569002'} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Payment Summary */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 4, 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px'
        }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 'bold', 
            mb: 3,
            color: theme.palette.primary.main
          }}>
            Payment Summary
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                backgroundColor: theme.palette.grey[100],
                p: 2,
                borderRadius: '6px'
              }}>
                <DetailItem label="Base Fare" value={`₹${data.invoiceAmount || '0'}`} />
                <DetailItem label="Taxes & Fees" value="₹0" />
                <Divider sx={{ my: 1 }} />
                <DetailItem 
                  label="Total Amount" 
                  value={`₹${data.invoiceAmount || '0'}`} 
                  important
                  large
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                backgroundColor: theme.palette.grey[100],
                p: 2,
                borderRadius: '6px'
              }}>
                <DetailItem label="Payment Method" value="Online Payment" />
                <DetailItem label="Transaction ID" value={data.invoiceNo} />
                <DetailItem label="Payment Status" value="Success" />
              </Box>
            </Grid>
          </Grid>
        </Paper>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Need help? Call 9021569002 or email support@edsonfintech.com
          </Typography>

        {/* Footer with buttons container */}
        <Box 
          id="pdf-buttons-container"
          sx={{ 
            display: 'flex', 
            justifyContent: 'end',
            alignItems: 'center',
            mt: 4,
            pt: 3,
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
 
          <Box>
            <Button 
              variant="contained" 
              startIcon={isGeneratingPDF ? <CircularProgress size={20} /> : <Download />}
              onClick={handleDownload}
              disabled={isGeneratingPDF}
              sx={{ 
                mr: 2,
                backgroundColor: theme.palette.success.main,
                '&:hover': { backgroundColor: theme.palette.success.dark }
              }}
            >
              {isGeneratingPDF ? 'Generating...' : 'Download Ticket'}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<Close />}
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const DetailItem = ({ label, value, important = false, large = false }) => (
  <Box sx={{ mb: large ? 2 : 1 }}>
    <Typography variant="caption" sx={{ 
      display: 'block',
      color: 'text.secondary',
      fontSize: '0.75rem',
      lineHeight: 1.2
    }}>
      {label}
    </Typography>
    <Typography variant={large ? 'h6' : 'body1'} sx={{ 
      fontWeight: important ? 700 : 500,
      color: important ? 'primary.main' : 'text.primary',
      wordBreak: 'break-word',
      lineHeight: 1.3
    }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

export default BusTicketInvoice;
